import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

// Segmento (lo que va DESPUES del slug: /<slug>/<screen>) de las pantallas
// operativas de STAFF donde el traductor del navegador NO debe actuar. El motor de
// Google/Brave mutila datos criticos: convertia el metodo "Nequi" en "De ninguna
// manera", y puede alterar nombres de productos/clientes o montos en plena
// operacion. El menu publico y el kiosko (cara al cliente) quedan FUERA a proposito
// para que un cliente extranjero pueda traducir.
//
// Mantener sincronizado con app.routes.ts / restaurant.routes.ts: si se agrega una
// pantalla operativa nueva, sumar su segmento aca (unica fuente de verdad).
const OPERATIONAL_SCREENS = new Set<string>([
  'pos',
  'pos-simple',
  'mesero',
  'cocina',
  'barra'
]);

/**
 * Pone `translate="no"` en <html> mientras la ruta activa es una pantalla
 * operativa, y lo quita al salir. A nivel <html> el navegador salta TODO el
 * documento — incluidos los `p-dialog`/`p-select` de PrimeNG que se montan en
 * <body> via `appendTo="body"` (fuera del subarbol del componente). Por eso se
 * hace aca y no con un `host: { translate: 'no' }` por componente, que dejaria los
 * popups sin cubrir.
 */
@Injectable({ providedIn: 'root' })
export class OperationalNoTranslateService {
  private readonly doc = inject(DOCUMENT);
  private readonly router = inject(Router);

  init(): void {
    const html = this.doc?.documentElement;
    if (!html) return; // SSR / entorno sin DOM

    // Estado inicial desde la URL real (en carga directa a /<slug>/pos el
    // router.url aun puede ser '/'); despues se sincroniza por navegacion.
    this.apply(this.doc.location?.pathname ?? this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.apply(e.urlAfterRedirects));
  }

  private apply(url: string): void {
    const html = this.doc.documentElement;
    if (this.isOperational(url)) {
      html.setAttribute('translate', 'no');
    } else {
      html.removeAttribute('translate');
    }
  }

  private isOperational(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(s => s.length > 0);
    // Las pantallas operativas viven en /<slug>/<screen>: el screen es el 2do
    // segmento. Asi /owner/pos-users o /superadmin/... no dan falso positivo.
    return segments.length >= 2 && OPERATIONAL_SCREENS.has(segments[1]);
  }
}
